import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, Niche } from '../api/client';
import { TrendingUp, Check, X, Sparkles, ExternalLink, Bot, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type ReviewQueueProps = {
  limit?: number;
};

export default function ReviewQueue({ limit = 5 }: ReviewQueueProps) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['review-queue', limit],
    queryFn: () => api.niches.reviewQueue(limit),
  });

  const updateNiche = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.niches.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['niches'] });
    },
  });

  const handleApprove = (niche: Niche) => {
    updateNiche.mutate(
      { id: niche.id, status: 'researched' },
      {
        onSuccess: () => {
          toast.success(`"${niche.keyword}" approved and ready for batch generation`);
        },
      }
    );
  };

  const handleIgnore = (niche: Niche) => {
    updateNiche.mutate(
      { id: niche.id, status: 'ignored' },
      {
        onSuccess: () => {
          toast.success(`"${niche.keyword}" moved to ignored`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Auto-Scout Review Queue</h3>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">Failed to load review queue</p>
        </div>
      </div>
    );
  }

  const niches = data?.niches ?? [];

  if (niches.length === 0) {
    return null; // Don't show if no items to review
  }

  return (
    <div className="card border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Auto-Scout Review Queue</h3>
            <p className="text-sm text-gray-500">
              {niches.length} niche{niches.length !== 1 ? 's' : ''} discovered by AI
            </p>
          </div>
        </div>
        <Link
          to="/research?source=auto-scout"
          className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
        >
          View all
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="space-y-3">
        {niches.map((niche) => {
          const score = Math.round(niche.trend_score * 100);
          const scoreColor =
            score >= 70
              ? 'text-green-600 bg-green-100'
              : score >= 50
                ? 'text-amber-600 bg-amber-100'
                : 'text-gray-600 bg-gray-100';

          return (
            <div
              key={niche.id}
              className="bg-white rounded-lg border border-purple-100 p-3 flex items-center gap-3"
            >
              {/* Score Badge */}
              <div
                className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold',
                  scoreColor
                )}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                {score}%
              </div>

              {/* Keyword */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{niche.keyword}</div>
                {niche.product_type && niche.product_type !== 'all' && (
                  <span className="text-xs text-gray-500 capitalize">{niche.product_type}</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Link
                  to={`/batch?niche_id=${niche.id}`}
                  className="btn btn-sm bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-1"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Generate
                </Link>
                <button
                  onClick={() => handleApprove(niche)}
                  disabled={updateNiche.isPending}
                  className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                  title="Approve"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleIgnore(niche)}
                  disabled={updateNiche.isPending}
                  className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                  title="Ignore"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="mt-4 pt-4 border-t border-purple-100 flex items-center justify-between text-sm">
        <span className="text-gray-500">
          Avg score:{' '}
          {Math.round(niches.reduce((acc, n) => acc + n.trend_score * 100, 0) / niches.length)}%
        </span>
        <Link
          to="/batch"
          className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
        >
          Batch generate from all
          <Sparkles className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
